/**
 * EtichetteLottoModal — Modal per stampare un lotto misto di etichette.
 * L'utente ha già selezionato N articoli via checkbox; qui può:
 *   - regolare le copie per ogni articolo (+/- o input diretto)
 *   - scegliere la dimensione etichetta (small/medium/large)
 *   - rimuovere articoli dal carrello
 *   - vedere il totale etichette e n. pagine stimato
 *   - stampare il PDF combinato
 */
import React from "react";
import { toast } from "sonner";
import { X, Printer, Minus, Plus, Trash2, Package, Layers } from "lucide-react";
import api from "@/api";

const SIZE_INFO = {
  small:  { label: "Piccola (4×10)", per_page: 40 },
  medium: { label: "Media (3×6)",    per_page: 18 },
  large:  { label: "Grande (2×4)",    per_page: 8  },
};

export default function EtichetteLottoModal({ items, onClose, onCleared }) {
  const [cart, setCart] = React.useState(() =>
    items.map((m) => ({ ...m, copies: 1 }))
  );
  const [size, setSize] = React.useState("medium");
  const [downloading, setDownloading] = React.useState(false);

  const setCopies = (id, n) =>
    setCart((prev) =>
      prev.map((it) => (it.materiale_id === id ? { ...it, copies: Math.max(1, Math.min(96, Number(n) || 1)) } : it))
    );
  const remove = (id) => setCart((prev) => prev.filter((it) => it.materiale_id !== id));

  const totalCopies = cart.reduce((s, it) => s + (Number(it.copies) || 0), 0);
  const perPage = SIZE_INFO[size].per_page;
  const pages = Math.max(1, Math.ceil(totalCopies / perPage));

  const stampa = async () => {
    if (cart.length === 0) return;
    if (totalCopies > 500) {
      toast.error("Massimo 500 etichette per lotto");
      return;
    }
    setDownloading(true);
    try {
      const payload = {
        size,
        items: cart.map((it) => ({ materiale_id: it.materiale_id, copies: Number(it.copies) || 1 })),
      };
      const res = await api.post("/magazzino/materiali/etichette-lotto.pdf", payload, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(`PDF generato · ${totalCopies} etichette su ${pages} pagin${pages === 1 ? "a" : "e"}`);
      onCleared?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-etichette-lotto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl max-h-[92vh] rounded-md shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="bg-slate-900 text-white p-4 flex items-center gap-3">
          <Layers className="w-6 h-6 text-cyan-400" />
          <div className="flex-1">
            <div className="overline text-cyan-400">Stampa etichette · lotto misto</div>
            <div className="font-display font-bold text-lg">
              {cart.length} articolo{cart.length === 1 ? "" : "i"} nel carrello · {totalCopies} etichett{totalCopies === 1 ? "a" : "e"}
            </div>
          </div>
          <button onClick={onClose} data-testid="btn-close-lotto" className="p-2 hover:bg-slate-800 rounded-sm">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap bg-slate-50">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Dimensione</label>
          {Object.entries(SIZE_INFO).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setSize(k)}
              data-testid={`size-${k}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-sm border ${
                size === k
                  ? "bg-cyan-700 text-white border-cyan-700"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
              }`}
            >
              {v.label}
            </button>
          ))}
          <div className="ml-auto text-xs text-slate-600">
            ≈ <b>{pages}</b> pagin{pages === 1 ? "a" : "e"} A4
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              Carrello vuoto.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-widest sticky top-0">
                <tr>
                  <th className="text-left p-3">Articolo</th>
                  <th className="text-center p-3 w-40">Copie</th>
                  <th className="p-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {cart.map((it) => (
                  <tr key={it.materiale_id} className="border-t border-slate-100" data-testid={`lotto-row-${it.materiale_id}`}>
                    <td className="p-3">
                      <div className="font-bold text-slate-900">{it.nome}</div>
                      <div className="font-mono text-[11px] text-slate-500">
                        {it.barcode || it.codice || it.materiale_id}
                        {it.ubicazione && ` · 📍 ${it.ubicazione}`}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setCopies(it.materiale_id, Number(it.copies) - 1)}
                          data-testid={`lotto-minus-${it.materiale_id}`}
                          disabled={Number(it.copies) <= 1}
                          className="p-1 rounded-sm bg-slate-200 hover:bg-slate-300 disabled:opacity-40"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          max="96"
                          value={it.copies}
                          onChange={(e) => setCopies(it.materiale_id, e.target.value)}
                          data-testid={`lotto-copies-${it.materiale_id}`}
                          data-no-uppercase
                          className="w-14 text-center font-mono font-black text-lg border border-slate-300 rounded-sm py-0.5"
                        />
                        <button
                          onClick={() => setCopies(it.materiale_id, Number(it.copies) + 1)}
                          data-testid={`lotto-plus-${it.materiale_id}`}
                          disabled={Number(it.copies) >= 96}
                          className="p-1 rounded-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-700 disabled:opacity-40"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => remove(it.materiale_id)}
                        data-testid={`lotto-remove-${it.materiale_id}`}
                        className="p-1.5 text-slate-400 hover:text-rose-600 rounded-sm"
                        title="Rimuovi dal carrello"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
          <button
            onClick={onClose}
            className="text-xs px-3 py-2 border border-slate-300 hover:bg-slate-100 rounded-sm"
          >
            Annulla
          </button>
          <div className="flex-1" />
          <button
            onClick={stampa}
            disabled={downloading || cart.length === 0}
            data-testid="btn-stampa-lotto"
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 text-white text-sm font-bold rounded-sm"
          >
            <Printer className="w-4 h-4" />
            {downloading ? "Genero PDF…" : `Stampa ${totalCopies} etichett${totalCopies === 1 ? "a" : "e"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
